import { useCallback, useState } from 'react'
import { flatten, dateTimeStampFormat } from 'shared/util'

import { Button, Stack, Typography } from '@mui/material'
import { AlarmAdd as ExtendTimeIcon } from '@mui/icons-material'
import { CloseIcon, TrushIcon } from 'shared/icons'

import { InviteResult, useDeleteInvite, useExtendInvite } from 'interface/user/invite'
import { ConfirmDialog } from 'components/dialog/confirm'
import { DialogBase } from 'components/dialog'
import { Section } from 'components/controls/section'

import { MailAddress } from 'features/kojin/components/mailAddress'

type EditInviteDialogProps = {
  invite: InviteResult
  onDeleteInvite: (inviteId: number) => void
  onExtendInvite: (invite: InviteResult) => void
  onClose: () => void
}
export const EditInviteDialog = ({
  invite,
  onDeleteInvite,
  onExtendInvite,
  onClose
}: EditInviteDialogProps) => {
  const [modal, setModal] = useState<'delete' | null>(null)
  const onDeleted = useCallback(
    (deletedId: number) => {
      onDeleteInvite(deletedId)
      onClose()
    },
    [onClose, onDeleteInvite]
  )
  const onExtended = useCallback(
    (extended: InviteResult) => {
      onExtendInvite(extended)
      onClose()
    },
    [onClose, onExtendInvite]
  )
  const [deleteState, deleteInvite] = useDeleteInvite({
    onDeleted
  })
  const [extendState, extendInvite] = useExtendInvite({
    onPosted: onExtended
  })
  return (
    <>
      <DialogBase
        open
        isLoading={deleteState.posting || extendState.posting}
        error={flatten([deleteState.error, extendState.error])}
        title="招待"
        body={
          <Stack direction="column">
            <Section label="ユーザー名" labelProps={{ py: 1.5 }}>
              {invite.userName ? (
                <Typography variant='body1'>{invite.userName}</Typography>
              ) : (
                <Typography variant='body1' color="textDisabled">-</Typography>
              )}
            </Section>
            <Section label="メールアドレス" labelProps={{ py: 1.5 }}>
              <MailAddress address={invite.email} mailStatus='OK'/>
            </Section>
            <Section label="有効期限" labelProps={{ py: 2 }}>
              {invite.effectiveTime ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body1">{dateTimeStampFormat(invite.effectiveTime)}</Typography>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => extendInvite(invite.inviteId)}
                    startIcon={<ExtendTimeIcon/>}
                  >
                    延長
                  </Button>
                </Stack>
              ) : (
                <Typography variant='body1' color="textDisabled">-</Typography>
              )}
            </Section>
          </Stack>
        }
        action={{
          left: (
            <Button
              variant="contained"
              color="error"
              startIcon={<TrushIcon/>}
              onClick={() => setModal("delete")}
            >
              削除
            </Button>
          ),
          right: (
            <Button
              variant="text"
              startIcon={<CloseIcon/>}
              onClick={onClose}
            >
              閉じる
            </Button>
          )
        }}
        onClose={onClose}
      >
      </DialogBase>
      {modal === 'delete' ? (
        <ConfirmDialog
          isLoading={deleteState.posting}
          message={[
            `${invite.email} の招待を中止します。`,
            '招待されたユーザーが登録しようとしてもエラーとなります。',
            '招待を中止してもよろしいですか？'
          ].join('\n')}
          ok={{
            label: 'はい',
            onClick: () => {
              deleteInvite(invite.inviteId)
              setModal(null)
            }
          }}
          cancel={{
            label: 'いいえ',
            onClick: () => setModal(null)
          }}
        />
      ) : <></>}
    </>
  )
}
